/**
 * Admin Hub Quick Access Component
 * Shows pinned favorites and recent items for quick navigation
 */
import React from 'react';
import { Box, Typography, IconButton, Collapse, Tooltip } from '@mui/material';
import {
  Star as StarIcon,
  History as HistoryIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import { useAdminHub } from './context/AdminHubContext';
import { ADMIN_HUB_DESIGN } from '../../config/adminHubConfig';

const MAX_FAVORITES_SHOWN = 5;
const MAX_RECENTS_SHOWN = 5;

export const AdminHubQuickAccess: React.FC = () => {
  const {
    favorites,
    recentItems,
    quickAccessCollapsed,
    toggleQuickAccessCollapsed,
    navigateTo,
    activePath,
  } = useAdminHub();

  // Don't show if no favorites or recents
  if (favorites.length === 0 && recentItems.length === 0) {
    return null;
  }

  // Filter recents to exclude items that are already in favorites
  const favoritePaths = new Set(favorites.map(f => f.path));
  const filteredRecents = recentItems
    .filter(r => !favoritePaths.has(r.path))
    .slice(0, MAX_RECENTS_SHOWN);

  const displayFavorites = favorites.slice(0, MAX_FAVORITES_SHOWN);

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
      {/* Header */}
      <Box
        onClick={toggleQuickAccessCollapsed}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 0.75,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: ADMIN_HUB_DESIGN.COLORS.HOVER_BG,
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: '0.7rem',
          }}
        >
          Quick Access
        </Typography>
        <IconButton size="small" sx={{ padding: 0.25 }}>
          {quickAccessCollapsed ? (
            <ExpandIcon sx={{ fontSize: 16 }} />
          ) : (
            <CollapseIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Box>

      <Collapse in={!quickAccessCollapsed}>
        {/* Favorites */}
        {displayFavorites.length > 0 && (
          <Box sx={{ pb: 0.5 }}>
            {displayFavorites.map(fav => {
              const isActive = activePath === fav.path;
              return (
                <Box
                  key={fav.path}
                  onClick={() => navigateTo(fav.path)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 0.5,
                    cursor: 'pointer',
                    backgroundColor: isActive ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM_BG : 'transparent',
                    borderLeft: isActive ? `3px solid ${ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM}` : '3px solid transparent',
                    '&:hover': {
                      backgroundColor: isActive
                        ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM_BG
                        : ADMIN_HUB_DESIGN.COLORS.HOVER_BG,
                    },
                  }}
                >
                  <StarIcon sx={{ fontSize: 14, color: '#F5A800' }} />
                  <Tooltip title={fav.section} placement="right">
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.8125rem',
                        fontWeight: isActive ? 500 : 400,
                        color: isActive ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM : 'text.primary',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {fav.label}
                    </Typography>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Recents */}
        {filteredRecents.length > 0 && (
          <Box sx={{ pb: 0.5 }}>
            {displayFavorites.length > 0 && (
              <Box sx={{ borderTop: '1px dashed', borderColor: 'divider', mx: 2, my: 0.5 }} />
            )}
            {filteredRecents.map(recent => {
              const isActive = activePath === recent.path;
              return (
                <Box
                  key={recent.path}
                  onClick={() => navigateTo(recent.path)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 0.5,
                    cursor: 'pointer',
                    backgroundColor: isActive ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM_BG : 'transparent',
                    borderLeft: isActive ? `3px solid ${ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM}` : '3px solid transparent',
                    '&:hover': {
                      backgroundColor: isActive
                        ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM_BG
                        : ADMIN_HUB_DESIGN.COLORS.HOVER_BG,
                    },
                  }}
                >
                  <HistoryIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Tooltip title={recent.section} placement="right">
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.8125rem',
                        fontWeight: isActive ? 500 : 400,
                        color: isActive ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM : 'text.secondary',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {recent.label}
                    </Typography>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default AdminHubQuickAccess;
